import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Row,
  Col,
  message,
  Pagination,
  Typography,
  Popconfirm,
  Tooltip,
  Statistic,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  EuroCircleOutlined,
  HomeOutlined,
  InboxOutlined,
  TruckOutlined,
  DownOutlined,
  ReloadOutlined,
  FileAddOutlined,
  BranchesOutlined,
  ImportOutlined, RobotOutlined, FileTextOutlined, NodeIndexOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { UnifiedShipment, UnifiedShipmentType } from '../types.ts';
import { unifiedShipmentsAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ShipmentCreateModal from '../components/ShipmentCreateModal.tsx';
import ShipmentAIImportModal from '../components/ShipmentAIImportModal.tsx';
import ShipmentEditModal from '../components/ShipmentEditModal.tsx';
import ShipmentProductsModal from '../components/ShipmentProductsModal.tsx';
import CustomsSplitModal from '../components/CustomsSplitModal.tsx';
import ShipmentLoadModal from '../components/ShipmentLoadModal.tsx';
import SupplierInvoicesModal from '../components/SupplierInvoicesModal.tsx';
import TransitDocumentsModal from '../components/TransitDocumentsModal.tsx';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface ShipmentStatus {
  id: number;
  code: string;
  description: string;
}

interface DashboardStats {
  total_shipments: number;
  extra_ue: number;
  ue: number;
  nazionale: number;
  in_preparation: number;
  shipped: number;
  in_transit: number;
  arrived: number;
}

// Sequenza obbligatoria degli stati spedizione (specchio di SHIPMENT_STATUS_TRANSITIONS
// in backend/routes/unified_shipments.py): codice stato attuale → codici selezionabili.
// "arrived" e "completed" non hanno passaggi manuali: l'evasione è automatica.
const SHIPMENT_STATUS_NEXT: Record<string, string[]> = {
  in_preparation: ['shipped'],
  shipped: ['in_transit'],
  in_transit: ['arrived'],
};

const UnifiedShipments: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('unified-shipments');
  // Stati
  const [shipments, setShipments] = useState<UnifiedShipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Stats
  const [stats, setStats] = useState<DashboardStats | null>(null);

  // Filtri
  const [filters, setFilters] = useState({
    shipment_type: undefined as UnifiedShipmentType | undefined,
    search: '',
    shipment_code: '',
    vehicle_plate: '',
    status_id: undefined as number | undefined,
    supplier_order_number: '',
    product: '',
    invoice_number: ''
  });
  // Fatture e packing list del fornitore (v3.8.0)
  const [invoicesShipment, setInvoicesShipment] = useState<UnifiedShipment | null>(null);
  // Documenti di transito T1 (v3.10.0)
  const [transitShipment, setTransitShipment] = useState<UnifiedShipment | null>(null);

  // Lookup
  const [statuses, setStatuses] = useState<ShipmentStatus[]>([]);

  // Modals
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [aiImportVisible, setAiImportVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [productsModalVisible, setProductsModalVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<UnifiedShipment | null>(null);

  // Modal documenti doganali (split IM7/IM4)
  const [customsModalVisible, setCustomsModalVisible] = useState(false);
  const [shipmentForCustoms, setShipmentForCustoms] = useState<UnifiedShipment | null>(null);

  // Modal carico in magazzino (NAZIONALE/UE, senza dogana)
  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [shipmentForLoad, setShipmentForLoad] = useState<UnifiedShipment | null>(null);

  // Caricamento dati
  useEffect(() => {
    loadStatuses();
    loadStats();
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const loadStatuses = async () => {
    try {
      const response = await unifiedShipmentsAPI.getStatuses();
      setStatuses(response || []);
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Il backend risponde con { total, by_type: {EXTRA_UE: n, ...}, by_status: {codice: {count}} }:
      // qui si ricavano i campi mostrati nelle card (prima letti con nomi inesistenti → sempre 0)
      const response = await unifiedShipmentsAPI.getDashboardStats();
      const byType = response?.by_type || {};
      const byStatus = response?.by_status || {};
      const statusCount = (code: string) => byStatus[code]?.count ?? 0;
      setStats({
        total_shipments: response?.total ?? 0,
        extra_ue: byType.EXTRA_UE ?? 0,
        ue: byType.UE ?? 0,
        nazionale: byType.NAZIONALE ?? 0,
        in_preparation: statusCount('in_preparation'),
        shipped: statusCount('shipped'),
        in_transit: statusCount('in_transit'),
        arrived: statusCount('arrived'),
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const skip = (page - 1) * pageSize;

      const params: any = {
        skip,
        limit: pageSize,
        ...(filters.shipment_type && { shipment_type: filters.shipment_type }),
        ...(filters.search && { search: filters.search }),
        ...(filters.shipment_code && { shipment_code: filters.shipment_code }),
        ...(filters.vehicle_plate && { vehicle_plate: filters.vehicle_plate }),
        ...(filters.status_id && { status_id: filters.status_id }),
        ...(filters.supplier_order_number && { supplier_order_number: filters.supplier_order_number }),
        ...(filters.product && { product: filters.product }),
        ...(filters.invoice_number && { invoice_number: filters.invoice_number })
      };

      const response = await unifiedShipmentsAPI.getUnifiedShipments(params);
      setShipments(response.shipments || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Error loading unified shipments:', error);
      message.error(error.response?.data?.detail || 'Errore nel caricamento delle spedizioni');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await unifiedShipmentsAPI.deleteUnifiedShipment(id);
      message.success('Spedizione eliminata con successo');
      loadData();
      loadStats();
    } catch (error: any) {
      console.error('Error deleting unified shipment:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'eliminazione della spedizione');
    }
  };

  const handleGenerateCustoms = (shipment: UnifiedShipment) => {
    setShipmentForCustoms(shipment);
    setCustomsModalVisible(true);
  };

  const handleLoadShipment = (shipment: UnifiedShipment) => {
    setShipmentForLoad(shipment);
    setLoadModalVisible(true);
  };

  const handleStatusChange = async (shipmentId: number, newStatusId: number) => {
    try {
      await unifiedShipmentsAPI.updateUnifiedShipment(shipmentId, {
        status_id: newStatusId
      });
      message.success('Stato aggiornato con successo');
      loadData();
      loadStats();
    } catch (error: any) {
      console.error('Error updating status:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento dello stato');
    }
  };

  const getShipmentTypeIcon = (type: UnifiedShipmentType) => {
    switch (type) {
      case 'EXTRA_UE':
        return <GlobalOutlined />;
      case 'UE':
        return <EuroCircleOutlined />;
      case 'NAZIONALE':
        return <HomeOutlined />;
      default:
        return <TruckOutlined />;
    }
  };

  const getShipmentTypeColor = (type: UnifiedShipmentType) => {
    switch (type) {
      case 'EXTRA_UE':
        return 'blue';
      case 'UE':
        return 'green';
      case 'NAZIONALE':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getShipmentTypeLabel = (type: UnifiedShipmentType) => {
    switch (type) {
      case 'EXTRA_UE':
        return 'Extra-UE';
      case 'UE':
        return 'UE';
      case 'NAZIONALE':
        return 'Nazionale';
      default:
        return type;
    }
  };

  const getStatusColor = (statusCode: string) => {
    switch (statusCode) {
      case 'in_preparation':
        return 'default';
      case 'shipped':
        return 'processing';
      case 'in_transit':
        return 'warning';
      case 'arrived':
        return 'success';
      case 'completed':
        return 'green';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<UnifiedShipment> = [
    {
      title: 'Codice',
      dataIndex: 'shipment_code',
      key: 'shipment_code',
      width: 140,
      fixed: 'left',
      render: (code: string, record) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Text strong>{code}</Text>
            {record.sibling_count && record.sibling_count > 1 && (
              <Tooltip title={`${record.sibling_count} mezzi con lo stesso codice`}>
                <Tag color="blue" style={{ marginRight: 0, fontSize: 11, padding: '0 4px' }}>
                  <BranchesOutlined /> {record.sibling_count}
                </Tag>
              </Tooltip>
            )}
          </Space>
          <Tag color={getShipmentTypeColor(record.shipment_type)} icon={getShipmentTypeIcon(record.shipment_type)}>
            {getShipmentTypeLabel(record.shipment_type)}
          </Tag>
        </Space>
      )
    },
    {
      title: 'Descrizione',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true
    },
    {
      title: 'Fornitore',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 150,
      render: (value: string, record) => (
        <Space direction="vertical" size={0}>
          <Text>{value || '-'}</Text>
          {(record.invoice_numbers?.length ?? 0) > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Fatt.: {record.invoice_numbers!.join(', ')}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Trasporto',
      key: 'transport',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.transport_type_description}</Text>
          {record.transport_document_number && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.transport_document_type}: {record.transport_document_number}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Targa/N° Container',
      dataIndex: 'vehicle_plate',
      key: 'vehicle_plate',
      width: 130,
      render: (value: string) => value || '-'
    },
    {
      title: 'Partenza',
      dataIndex: 'departure_date',
      key: 'departure_date',
      width: 110,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Arrivo Prev.',
      dataIndex: 'expected_arrival_date',
      key: 'expected_arrival_date',
      width: 110,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Magazzino',
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
      width: 120
    },
    {
      title: 'Stato',
      key: 'status',
      width: 150,
      render: (_, record) => {
        const currentStatus = statuses.find(s => s.id === record.status_id);
        const isCompleted = currentStatus?.code === 'completed';

        // Senza permesso di scrittura lo stato non è modificabile: solo Tag statico
        if (isCompleted || !canEdit) {
          return (
            <Tag color={getStatusColor('completed')}>
              {record.status_name || currentStatus?.description}
            </Tag>
          );
        }

        // Sequenza obbligatoria (specchio del backend): In Preparazione → Spedito →
        // In Transito → Arrivato, senza salti. "Evaso" (completed) NON è selezionabile
        // a mano: è automatico dal Carico merce (NAZIONALE/UE) o dall'IM7/IM4 (EXTRA_UE).
        // Un salto (es. In Preparazione → Arrivato) lasciava la merce in produzione e
        // l'IM7 la ricreava dal nulla.
        const nextCodes = SHIPMENT_STATUS_NEXT[currentStatus?.code || ''] || [];
        const nextStatuses = statuses.filter(s => nextCodes.includes(s.code));
        if (nextStatuses.length === 0) {
          return (
            <Tag color={getStatusColor(currentStatus?.code || '')}>
              {record.status_name || currentStatus?.description}
            </Tag>
          );
        }

        return (
          <Dropdown
            menu={{
              items: nextStatuses.map(s => ({
                key: s.id.toString(),
                label: s.description,
                onClick: () => handleStatusChange(record.id, s.id)
              }))
            }}
            trigger={['click']}
          >
            <Tag
              color={getStatusColor(currentStatus?.code || '')}
              style={{ cursor: 'pointer' }}
            >
              {record.status_name || currentStatus?.description} <DownOutlined />
            </Tag>
          </Dropdown>
        );
      }
    },
    {
      title: 'Items',
      key: 'items_count',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Visualizza Packing List">
          <Button
            type="text"
            size="small"
            onClick={() => {
              setSelectedShipment(record);
              setProductsModalVisible(true);
            }}
          >
            <Tag color="cyan">{record.items?.length || 0}</Tag>
          </Button>
        </Tooltip>
      )
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
          <Space size="small">
            {/* Pulsante "Genera documenti doganali" (split IM7/IM4) - solo EXTRA_UE arrivate */}
            {canEdit && record.shipment_type === 'EXTRA_UE' && record.status_code === 'arrived' && (
              <Tooltip title={(record.undeclared_quantity ?? 0) > 0
                ? `Genera dichiarazioni H2 (IM7) / H1 (IM4): ${record.undeclared_quantity} pz da dichiarare`
                : "Tutta la merce della spedizione è già in una dichiarazione"}>
                <Button
                  type={(record.undeclared_quantity ?? 0) > 0 ? "primary" : "default"}
                  size="small"
                  icon={<FileAddOutlined />}
                  onClick={() => handleGenerateCustoms(record)}
                  disabled={(record.undeclared_quantity ?? 0) <= 0}
                >
                  H2/H1
                </Button>
              </Tooltip>
            )}
            {/* Pulsante "Documento di carico" (DDT / Packing List / Altro in bozza) - solo NAZIONALE/UE arrivate */}
            {canEdit && (record.shipment_type === 'NAZIONALE' || record.shipment_type === 'UE') && record.status_code === 'arrived' && (
              <Tooltip
                title={
                  record.has_draft_document
                    ? 'C\'è già un documento di carico in bozza: elaboralo in Documenti di Carico'
                    : (record.transit_residual ?? 0) <= 0
                      ? 'Nessuna merce in transito da caricare'
                      : 'Crea il documento di carico (DDT, Packing List o Altro) in bozza'
                }
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<ImportOutlined />}
                  onClick={() => handleLoadShipment(record)}
                  disabled={!!record.has_draft_document || (record.transit_residual ?? 0) <= 0}
                >
                  Documento
                </Button>
              </Tooltip>
            )}
            {record.shipment_type === 'EXTRA_UE' && (
              <Tooltip title="Documenti di transito T1 (porto → deposito)">
                <Button
                  type="text"
                  icon={<NodeIndexOutlined />}
                  onClick={() => setTransitShipment(record)}
                />
              </Tooltip>
            )}
            <Tooltip title="Fatture e packing list del fornitore">
              <Button
                type="text"
                icon={<FileTextOutlined />}
                onClick={() => setInvoicesShipment(record)}
              />
            </Tooltip>
            <Tooltip title="Packing List">
            <Button
              type="text"
              icon={<InboxOutlined />}
              onClick={() => {
                setSelectedShipment(record);
                setProductsModalVisible(true);
              }}
            />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Modifica">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedShipment(record);
                  setEditModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {canEdit && (
            <Popconfirm
              title="Eliminare questa spedizione?"
              description="Questa azione non può essere annullata."
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <TruckOutlined style={{ marginRight: 12 }} />
            Gestione Spedizioni
          </Title>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { loadData(); loadStats(); }}
            style={{ marginRight: 8 }}
          >
            Aggiorna
          </Button>
        </Col>
      </Row>

      {/* Dashboard Stats */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Totale Spedizioni"
                value={stats.total_shipments}
                prefix={<TruckOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Row gutter={8}>
                <Col span={8}>
                  <Statistic
                    title="Extra-UE"
                    value={stats.extra_ue}
                    valueStyle={{ color: '#1890ff', fontSize: 20 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="UE"
                    value={stats.ue}
                    valueStyle={{ color: '#52c41a', fontSize: 20 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="NAZ"
                    value={stats.nazionale}
                    valueStyle={{ color: '#fa8c16', fontSize: 20 }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic
                    title="In Preparazione"
                    value={stats.in_preparation}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Spedite"
                    value={stats.shipped}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic
                    title="In Transito"
                    value={stats.in_transit}
                    valueStyle={{ color: '#faad14', fontSize: 20 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Arrivate"
                    value={stats.arrived}
                    valueStyle={{ color: '#52c41a', fontSize: 20 }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtri */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Tipo Spedizione"
              style={{ width: '100%' }}
              allowClear
              value={filters.shipment_type}
              onChange={(value) => {
                setFilters({ ...filters, shipment_type: value });
                setPage(1);
              }}
            >
              <Option value="EXTRA_UE">
                <GlobalOutlined style={{ marginRight: 4 }} /> Extra-UE
              </Option>
              <Option value="UE">
                <EuroCircleOutlined style={{ marginRight: 4 }} /> UE
              </Option>
              <Option value="NAZIONALE">
                <HomeOutlined style={{ marginRight: 4 }} /> Nazionale
              </Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Stato"
              style={{ width: '100%' }}
              allowClear
              value={filters.status_id}
              onChange={(value) => {
                setFilters({ ...filters, status_id: value });
                setPage(1);
              }}
            >
              {statuses.map(status => (
                <Option key={status.id} value={status.id}>
                  {status.description}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Cerca per codice, descrizione..."
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, search: value });
                setPage(1);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Codice Spedizione..."
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, shipment_code: value });
                setPage(1);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Targa / N° Container (spazi ignorati)..."
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, vehicle_plate: value });
                setPage(1);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Ordine Fornitore..."
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, supplier_order_number: value });
                setPage(1);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Prodotto (codice o descrizione)..."
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, product: value });
                setPage(1);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Fattura / Packing list fornitore..."
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, invoice_number: value });
                setPage(1);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            {canEdit && (
              <Space style={{ width: '100%' }}>
                <Tooltip title="Carica fattura, packing list e polizza di carico (BL) del fornitore tramite AI">
                  <Button
                    icon={<RobotOutlined />}
                    onClick={() => setAiImportVisible(true)}
                  >
                    Importa con AI
                  </Button>
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Nuova Spedizione
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      {/* Tabella */}
      <Card>
        <Table
          columns={columns}
          dataSource={shipments}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1730 }}
          size="small"
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(newPage) => setPage(newPage)}
            showSizeChanger={false}
            showTotal={(total) => `Totale ${total} spedizioni`}
          />
        </div>
      </Card>

      <ShipmentAIImportModal
        visible={aiImportVisible}
        onClose={() => setAiImportVisible(false)}
        onSuccess={() => {
          loadData();
          loadStats();
        }}
      />

      {/* Modal Creazione */}
      <ShipmentCreateModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={() => {
          loadData();
          loadStats();
        }}
      />

      {/* Modal Modifica Spedizione */}
      <ShipmentEditModal
        visible={editModalVisible}
        shipment={selectedShipment}
        onClose={() => {
          setEditModalVisible(false);
          setSelectedShipment(null);
        }}
        onSuccess={() => {
          loadData();
          loadStats();
        }}
      />

      {/* Modal Packing List */}
      <ShipmentProductsModal
        visible={productsModalVisible}
        shipment={selectedShipment}
        onClose={() => {
          setProductsModalVisible(false);
          setSelectedShipment(null);
        }}
        onSuccess={() => {
          loadData();
        }}
      />

      {/* Modal Fatture e packing list del fornitore */}
      <SupplierInvoicesModal
        visible={!!invoicesShipment}
        shipment={invoicesShipment}
        canEdit={canEdit}
        onClose={(changed) => {
          setInvoicesShipment(null);
          if (changed) loadData();
        }}
      />

      {/* Modal Documenti di transito T1 */}
      <TransitDocumentsModal
        visible={!!transitShipment}
        shipment={transitShipment}
        canEdit={canEdit}
        onClose={() => setTransitShipment(null)}
      />

      {/* Modal Generazione Documenti Doganali (split IM7/IM4) */}
      <CustomsSplitModal
        visible={customsModalVisible}
        shipment={shipmentForCustoms}
        onCancel={() => {
          setCustomsModalVisible(false);
          setShipmentForCustoms(null);
        }}
        onSuccess={() => {
          setCustomsModalVisible(false);
          setShipmentForCustoms(null);
          loadData();
        }}
      />

      {/* Modal Documento di carico in bozza (NAZIONALE/UE, senza dogana) */}
      <ShipmentLoadModal
        visible={loadModalVisible}
        shipment={shipmentForLoad}
        onCancel={() => {
          setLoadModalVisible(false);
          setShipmentForLoad(null);
        }}
        onSuccess={() => {
          setLoadModalVisible(false);
          setShipmentForLoad(null);
          loadData();
          loadStats();
        }}
      />
    </div>
  );
};

export default UnifiedShipments;
